import { Box, Button, DialogTitle, Divider, Stack } from "@mui/material";
import type { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useCallback, useMemo, useState } from "react";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import SelectParameterGrid from "@entrilia/shared/components/reporting/SelectParameterGrid";
import type { ParameterDictionary, ParameterDictionaryValue } from "../../../../../../api/types/workbookRuns.types";
import { ReportSource } from "../../../../../../api/types/workbookRuns.types";
import { toLabel } from "../../../../dashboard/utils/withValue";

interface Props {
  keyFieldName: string;
  dictionarySource: ReportSource;
  options: ParameterDictionary[];
  originalSelectedValues: ParameterDictionaryValue[];
  filterCaption: string;
  onBack: () => void;
  onUpdateFilter: (values: ParameterDictionary[]) => void;
}

const getDataRelatedColumnKeys = (values: ParameterDictionary[] | undefined) => {
  return Object.keys(values?.[0] || {}).filter((k) => k !== "id" && k !== "subValues");
};

const getFallbackValue = (value: unknown, reportSource: ReportSource, isKeyFieldColumn: boolean) => {
  if (reportSource === ReportSource.BI && isKeyFieldColumn) {
    return toLabel(null);
  }

  return value;
};

const getColumn = (field: string, reportSource: ReportSource, keyFieldName: string): GridColDef => {
  const column: GridColDef = {
    field: field,
    headerName: field,
    disableColumnMenu: true,
    flex: 1,
    disableReorder: true,
    valueFormatter: (value, _, col) => value ?? getFallbackValue(value, reportSource, col.field === keyFieldName),
  };

  return column;
};

const getPrimaryDataValue = (
  param: ParameterDictionary,
  keyFieldName: string | undefined
): ParameterDictionaryValue => {
  return param[keyFieldName as keyof ParameterDictionary] as ParameterDictionaryValue;
};

const WorkbookTextFilterValueSelector = ({
  dictionarySource,
  keyFieldName,
  options,
  originalSelectedValues,
  filterCaption,
  onBack,
  onUpdateFilter,
}: Props) => {
  const apiRef = useGridApiRef();
  const [search, setSearch] = useState("");
  const dataColumnKeys = useMemo(() => getDataRelatedColumnKeys(options), [options]);
  const [selectedValues, setSelectedValues] = useState<ParameterDictionaryValue[]>(() =>
    structuredClone(originalSelectedValues)
  );

  const columns = useMemo((): GridColDef[] => {
    return dataColumnKeys.map((col) => getColumn(col, dictionarySource, keyFieldName));
  }, [dictionarySource, keyFieldName, dataColumnKeys]);

  const filteredValues = useMemo(() => {
    if (!search) {
      return options;
    }
    return options.filter((v) =>
      dataColumnKeys.some((col) => toLabel(v[col])?.toLowerCase().includes(search.toLowerCase()))
    );
  }, [options, search, dataColumnKeys]);

  const handleValueSelect = useCallback(
    (newSelectedValues: GridRowSelectionModel) => {
      setSelectedValues((prev) => [
        ...filteredValues
          .filter((fv) => newSelectedValues.ids.has(fv.id))
          .map((fv) => getPrimaryDataValue(fv, keyFieldName)),
        ...prev.filter((sv) => !filteredValues?.some((v) => getPrimaryDataValue(v, keyFieldName) === sv)),
      ]);
    },
    [setSelectedValues, filteredValues, keyFieldName]
  );

  const selectedValueIds = useMemo(() => {
    return options.filter((v) => selectedValues.includes(getPrimaryDataValue(v, keyFieldName))).map((v) => v.id) || [];
  }, [selectedValues, options, keyFieldName]);

  return (
    <>
      <DialogTitle noWrap sx={{ mr: 2 }}>
        {filterCaption}
      </DialogTitle>
      <DialogCloseButton onClick={onBack} />
      <Stack sx={{ flex: 1, height: "100%" }}>
        <Box sx={{ display: "flex", px: 2, pt: 2, pb: 1.5 }}>
          <SearchField placeholder="Search" onSearch={setSearch} fullWidth />
        </Box>
        <Stack sx={{ flex: 1, px: 2, height: "100%", pb: 2 }}>
          <SelectParameterGrid
            apiRef={apiRef}
            columns={columns}
            rows={filteredValues}
            isLoading={false}
            onValueSelect={handleValueSelect}
            selectedValueIds={selectedValueIds}
            keyFieldName={keyFieldName}
            groupByField={undefined}
            singleSelect={false}
          />
        </Stack>
        <Divider />
        <Box sx={{ display: "flex", gap: 1, px: 2, py: 1, justifyContent: "flex-end" }}>
          <Button variant="text" color="secondary" size="small" onClick={onBack}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              onUpdateFilter(options.filter((v) => selectedValueIds.includes(v.id)));
              onBack();
            }}
          >
            Apply
          </Button>
        </Box>
      </Stack>
    </>
  );
};

export default WorkbookTextFilterValueSelector;
